/**
 * Normalized fields for the preloaded SIM congratulations dialog.
 * Populate from inventory API via {@link preloadedSimDetailsFromApiResponse}.
 */
export interface PreloadedSimCongratulationsDetails {
  /** Device manufacturer (e.g. AT&T inventory `device.make`). */
  deviceMake?: string | null;
  /** Device model name / code (e.g. inventory `device.model`). */
  deviceModel?: string | null;
  imei?: string | null;
  sim?: string | null;
  // condition?: string | null;
  plan?: string | null;
  securityPlan?: string | null;
}

export interface MobileSimInventoryPlanIncluded {
  name?: string | null;
  frequency?: number | string | null;
}

export interface MobileSimInventorySecurityPlanIncluded {
  securityPlanName?: string | null;
  planName?: string | null;
  name?: string | number | null;
}

export interface MobileSimInventoryRecord {
  planIncluded?: MobileSimInventoryPlanIncluded | null;
  securityPlanIncluded?: MobileSimInventorySecurityPlanIncluded | null;
  imeiNumber?: string | null;
  imei?: string | null;
  simNumber?: string | null;
  iccid?: string | null;
  sim?: string | null;
  vanityName?: string | null;
  deviceModel?: string | null;
  model?: string | null;
}

export interface MobileSimInventoryDevice {
  attCompatibility?: string | null;
  make?: string | null;
  model?: string | null;
}

export interface MobileSimInventoryPayload {
  simInventory?: MobileSimInventoryRecord | null;
  device?: MobileSimInventoryDevice | null;
}

export interface MobileSimInventoryBySimNumberResponse {
  success?: boolean;
  message?: string;
  data?: MobileSimInventoryPayload | MobileSimInventoryRecord | null;
}

type MobileSimInventoryParserInput =
  | MobileSimInventoryBySimNumberResponse
  | MobileSimInventoryPayload
  | MobileSimInventoryRecord
  | null
  | undefined;

type InventoryAndDevice = {
  simInventory: MobileSimInventoryRecord | null;
  device: MobileSimInventoryDevice | null;
};

function parsePositiveInt(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n =
    typeof value === "number" ? value : parseInt(String(value).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatPlanMonthsLabel(months: number): string {
  return months === 1 ? "1 Month" : `${months} Months`;
}

/** Tier label with explicit "Plan" after the data allowance, e.g. `1GB Plan - 3 Months`. */
export function formatPreloadedSimPlanFromInventory(
  planIncluded: MobileSimInventoryPlanIncluded | null | undefined,
  planFrequency: unknown,
): string | null {
  const name =
    typeof planIncluded?.name === "string" && planIncluded.name.trim()
      ? planIncluded.name.trim()
      : "";
  const months =
    parsePositiveInt(planFrequency) ?? parsePositiveInt(planIncluded?.frequency);

  const tierWithPlan = (() => {
    if (!name) return "";
    const lower = name.toLowerCase();
    if (/\bplan\b/.test(lower)) return name;
    return `${name} Plan`;
  })();

  if (tierWithPlan && months != null) {
    return `${tierWithPlan} - ${formatPlanMonthsLabel(months)}`;
  }
  if (tierWithPlan) return tierWithPlan;
  return null;
}

function formatSecurityPlanLabelFromInventory(
  inner: MobileSimInventoryRecord,
): string | null {
  const sp = inner.securityPlanIncluded;
  if (!sp) return null;
  const raw =
    (typeof sp.securityPlanName === "string" && sp.securityPlanName.trim()) ||
    (typeof sp.planName === "string" && sp.planName.trim()) ||
    (typeof sp.name === "string" && sp.name.trim()) ||
    "";
  if (!raw) return null;
  // const stripped = raw.replace(/^Aura\s+/i, "").trim();
  const stripped = raw.trim();
  return stripped || raw;
}

function inventoryDataFromPayload(
  data: MobileSimInventoryParserInput,
): MobileSimInventoryPayload | MobileSimInventoryRecord | null {
  if (data == null || typeof data !== "object") {
    return null;
  }
  const root = data as MobileSimInventoryBySimNumberResponse;
  const inner = root.data ?? data;
  return inner && typeof inner === "object"
    ? (inner as MobileSimInventoryPayload | MobileSimInventoryRecord)
    : null;
}

function inventoryAndDeviceFromPayload(
  data: MobileSimInventoryParserInput,
): InventoryAndDevice {
  const inner = inventoryDataFromPayload(data);
  if (!inner) {
    return { simInventory: null, device: null };
  }
  const payload = inner as MobileSimInventoryPayload;
  const hasWrappedInventory = Boolean(payload.simInventory || payload.device);
  const simInventory = hasWrappedInventory
    ? (payload.simInventory ?? null)
    : (inner as MobileSimInventoryRecord);
  const device = hasWrappedInventory ? (payload.device ?? null) : null;
  return { simInventory, device };
}

/**
 * Map mobile SIM inventory API payload into structured congratulations details.
 * Accepts full `{ success, message, data }` or a bare `data` object.
 */
export function preloadedSimDetailsFromApiResponse(
  data: MobileSimInventoryParserInput,
): PreloadedSimCongratulationsDetails {
  const { simInventory, device } = inventoryAndDeviceFromPayload(data);
  if (!simInventory) {
    return {};
  }

  const plan = formatPreloadedSimPlanFromInventory(
    simInventory.planIncluded,
    simInventory.planIncluded?.frequency,
  );

  const securityPlan = formatSecurityPlanLabelFromInventory(simInventory);

  const imei =
    (typeof simInventory.imeiNumber === "string" && simInventory.imeiNumber.trim()) ||
    (typeof simInventory.imei === "string" && simInventory.imei.trim()) ||
    null;

  const sim =
    (typeof simInventory.simNumber === "string" && simInventory.simNumber.trim()) ||
    (typeof simInventory.iccid === "string" && simInventory.iccid.trim()) ||
    (typeof simInventory.sim === "string" && simInventory.sim.trim()) ||
    null;

  const deviceMake =
    typeof device?.make === "string" && device.make.trim()
      ? device.make.trim()
      : null;
  const deviceModel =
    (typeof device?.model === "string" && device.model.trim()) ||
    (typeof simInventory.vanityName === "string" && simInventory.vanityName.trim()) ||
    (typeof simInventory.deviceModel === "string" && simInventory.deviceModel.trim()) ||
    (typeof simInventory.model === "string" && simInventory.model.trim()) ||
    null;

  return {
    deviceMake,
    deviceModel,
    imei,
    sim,
    // condition:
    //   typeof inner.condition === "string" && inner.condition.trim()
    //     ? inner.condition.trim()
    //     : null,
    plan,
    securityPlan,
  };
}

function buildDeviceCongratulationsRow(
  details: PreloadedSimCongratulationsDetails,
): string | null {
  const make = String(details.deviceMake || "").trim();
  const model = String(details.deviceModel || "").trim();
  if (make && model) return `Device - ${make} ${model}`;
  if (make) return `Device - ${make}`;
  if (model) return `Device - ${model}`;
  return null;
}

/**
 * Rows shown after preloaded SIM flow success: device (make + model), IMEI, SIM, plan, security plan.
 */
export function buildPreloadedSimCongratulationsRows(
  details: PreloadedSimCongratulationsDetails,
): string[] {
  return [
    buildDeviceCongratulationsRow(details),
    details.imei ? `IMEI Number - ${details.imei}` : null,
    details.sim ? `Sim - ${details.sim}` : null,
    // details.condition ? `Condition - ${details.condition}` : null,
    details.plan ? `Plan - ${details.plan}` : null,
    details.securityPlan ? `Security Plan - ${details.securityPlan}` : null,
  ].filter(Boolean) as string[];
}

/** Merge API-normalized details with fallbacks (e.g. form values when API omits a field). */
export function mergePreloadedSimCongratulationsDetails(
  api: PreloadedSimCongratulationsDetails,
  fallback: PreloadedSimCongratulationsDetails,
): PreloadedSimCongratulationsDetails {
  return {
    deviceMake: api.deviceMake ?? fallback.deviceMake,
    deviceModel: api.deviceModel ?? fallback.deviceModel,
    imei: api.imei ?? fallback.imei,
    sim: api.sim ?? fallback.sim,
    // condition: api.condition ?? fallback.condition,
    plan: api.plan ?? fallback.plan,
    securityPlan: api.securityPlan ?? fallback.securityPlan,
  };
}
